import React from 'react';
import { useTranslation } from 'react-i18next';

const INSTALL_STEPS = [
  'faq_panel7_img1_caption',
  'faq_panel7_img2_caption',
  'faq_panel7_img3_caption',
  'faq_panel7_img4_caption',
  'faq_panel7_img5_caption',
] as const;

const GUIDE_SECTIONS = [
  {
    id: 'install',
    titleKey: 'faq_panel7_install_title',
    bodyKey: 'faq_panel7_install_intro',
    stepsKey: 'faq_autoclose_install_steps',
    mock: 'chrome',
  },
  {
    id: 'settings',
    titleKey: 'faq_panel7_step2_title',
    bodyKey: 'faq_panel7_step2_content',
    stepsKey: 'faq_autoclose_settings_steps',
    mock: 'settings',
  },
  {
    id: 'usage',
    titleKey: 'faq_autoclose_usage_title',
    bodyKey: 'faq_autoclose_usage_body',
    stepsKey: 'faq_autoclose_usage_steps',
    mock: 'tabs',
  },
] as const;

function ChromeInstallMockup() {
  const { t } = useTranslation('translation');

  return (
    <div className="faq-autoclose-install" aria-hidden="true">
      {INSTALL_STEPS.map((captionKey, index) => (
        <div key={captionKey} className="faq-autoclose-install__step">
          <span className="faq-autoclose-install__num">{index + 1}</span>
          <div className="faq-autoclose-install__scene">
            {index === 0 ? (
              <div className="faq-autoclose-chrome faq-autoclose-chrome--address">
                <span className="faq-autoclose-chrome__bar">chrome://extensions</span>
              </div>
            ) : null}
            {index === 1 ? (
              <div className="faq-autoclose-chrome faq-autoclose-chrome--devmode">
                <span className="faq-autoclose-chrome__label">Developer mode</span>
                <span className="faq-autoclose-chrome__switch faq-autoclose-chrome__switch--on" />
              </div>
            ) : null}
            {index === 2 ? (
              <div className="faq-autoclose-chrome faq-autoclose-chrome--toolbar">
                <span className="faq-autoclose-chrome__btn faq-autoclose-chrome__btn--accent">
                  Load unpacked
                </span>
              </div>
            ) : null}
            {index === 3 ? (
              <div className="faq-autoclose-chrome faq-autoclose-chrome--folder">
                <span className="faq-autoclose-chrome__folder" />
                <span className="faq-autoclose-chrome__folder-name">
                  fishhunter-combined-extension
                </span>
              </div>
            ) : null}
            {index === 4 ? (
              <div className="faq-autoclose-chrome faq-autoclose-chrome--extension">
                <span className="faq-autoclose-chrome__ext-icon" />
                <span className="faq-autoclose-chrome__ext-copy">
                  <span className="faq-autoclose-chrome__ext-title">
                    Autopaste and Autoclose tables
                  </span>
                  <span className="faq-autoclose-chrome__ext-meta">Fish Hunter</span>
                </span>
                <span className="faq-autoclose-chrome__switch faq-autoclose-chrome__switch--on" />
              </div>
            ) : null}
          </div>
          <p className="faq-autoclose-install__caption">{t(captionKey)}</p>
        </div>
      ))}
    </div>
  );
}

function SettingsMockup() {
  const { t } = useTranslation('translation');

  return (
    <div className="faq-autoclose-settings" aria-hidden="true">
      <div className="faq-autoclose-settings__panel">
        <p className="faq-autoclose-settings__label">{t('inactive_tab_close')}</p>
        <p className="faq-autoclose-settings__hint">{t('inactive_tab_close_hint')}</p>
        <label className="faq-autoclose-settings__toggle">
          <span className="faq-autoclose-settings__switch faq-autoclose-settings__switch--on" />
          <span>{t('inactive_tab_close_enabled')}</span>
        </label>
        <span className="faq-autoclose-settings__select">
          {t('inactive_tab_close_minutes', { minutes: 5 })}
        </span>
      </div>
    </div>
  );
}

function TabsMockup() {
  return (
    <div className="faq-autoclose-tabs" aria-hidden="true">
      <div className="faq-autoclose-tabs__bar">
        <span className="faq-autoclose-tabs__tab faq-autoclose-tabs__tab--active">
          Fish Hunter · active
        </span>
        <span className="faq-autoclose-tabs__tab">Fish Hunter</span>
        <span className="faq-autoclose-tabs__tab">Fish Hunter</span>
      </div>
      <div className="faq-autoclose-tabs__body">
        <span className="faq-autoclose-tabs__pulse" />
        <span className="faq-autoclose-tabs__line faq-autoclose-tabs__line--short" />
        <span className="faq-autoclose-tabs__line" />
      </div>
      <div className="faq-autoclose-tabs__timers">
        <span className="faq-autoclose-tabs__timer faq-autoclose-tabs__timer--idle">5:00</span>
        <span className="faq-autoclose-tabs__timer faq-autoclose-tabs__timer--idle">5:00</span>
      </div>
    </div>
  );
}

const MOCKUPS = {
  chrome: ChromeInstallMockup,
  settings: SettingsMockup,
  tabs: TabsMockup,
} as const;

function renderSteps(steps: string[]) {
  return (
    <ol className="faq-layout-card__steps">
      {steps.map((step) => (
        <li key={step}>{step}</li>
      ))}
    </ol>
  );
}

const V2FAQAutoCloseGuide: React.FC = () => {
  const { t } = useTranslation('translation');
  const notes = t('faq_autoclose_notes', { returnObjects: true }) as string[];

  return (
    <div className="faq-autoclose-guide">
      {GUIDE_SECTIONS.map(({ id, titleKey, bodyKey, stepsKey, mock }) => {
        const Mockup = MOCKUPS[mock];
        const steps = t(stepsKey, { returnObjects: true }) as string[];

        return (
          <article key={id} className={`faq-layout-card faq-layout-card--autoclose-${id}`}>
            <div className="faq-layout-card__visual">
              <Mockup />
            </div>
            <div className="faq-layout-card__copy">
              <h3>{t(titleKey)}</h3>
              <p className="faq-layout-card__lead">{t(bodyKey)}</p>
              {Array.isArray(steps) && steps.length > 0 ? renderSteps(steps) : null}
            </div>
          </article>
        );
      })}
      <aside className="faq-layout-note">
        <h4>{t('faq_panel7_notes_title')}</h4>
        {Array.isArray(notes) && notes.length > 0 ? (
          <ul className="faq-autoclose-notes">
            {notes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        ) : (
          <p>{t('faq_panel7_settings_content')}</p>
        )}
      </aside>
    </div>
  );
};

export default V2FAQAutoCloseGuide;
